import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICasino } from 'constants/types';
import { listCasinos, CasinoListParams } from 'lib/api/casinos';

export function usePrevious<T = unknown>(value: T): T | undefined {
  const ref = useRef<T>(undefined);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

const DEFAULT_LIMIT = 12;

export const useCasinosPagination = ({
  skip,
  sortBy,
  collection,
  filters = {},
  searchQuery = '',
  submittedTimestamp,
  initialData = [],
  initialCount,
  limit = DEFAULT_LIMIT,
}: {
  skip?: boolean;
  sortBy?: string;
  collection?: string | string[] | undefined | null;
  filters?: Omit<CasinoListParams, 'page' | 'limit' | 'sortBy' | 'collections'>;
  searchQuery?: string;
  submittedTimestamp?: number;
  initialData?: ICasino[];
  initialCount?: number;
  limit?: number;
}) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ICasino[]>(initialData);
  const [count, setCount] = useState<number>(
    initialCount ?? (initialData.length > 0 ? initialData.length : -1)
  );
  const [isFetching, setIsFetching] = useState(false);

  const previousSearchQuery = usePrevious(searchQuery);
  const collectionKey = Array.isArray(collection)
    ? collection.join(',')
    : (collection ?? '');

  const requestParams = useMemo(
    () => ({
      sortBy,
      collections: collectionKey || undefined,
      searchQuery: searchQuery || undefined,
      ...filters,
    }),
    [sortBy, collectionKey, searchQuery, filters]
  );

  const fetchPage = useCallback(
    async (targetPage: number, replace: boolean) => {
      if (skip) {
        return;
      }

      setIsFetching(true);

      try {
        const response = await listCasinos({
          page: targetPage,
          limit,
          ...requestParams,
        });

        const results = response.results ?? [];
        const total = Number(response.count ?? results.length);

        setCount(total);
        setData((current) => (replace ? results : [...current, ...results]));
      } finally {
        setIsFetching(false);
      }
    },
    [skip, limit, requestParams]
  );

  const resetAndFetch = useCallback(() => {
    setPage(1);
    setData([]);
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    setData(initialData);
    setCount(
      initialCount ?? (initialData.length > 0 ? initialData.length : -1)
    );
    setPage(1);
  }, [collectionKey, initialData, initialCount]);

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      resetAndFetch();
    }
  }, [filters, searchQuery, submittedTimestamp, resetAndFetch]);

  useEffect(() => {
    if (
      previousSearchQuery &&
      previousSearchQuery.length !== searchQuery.length
    ) {
      resetAndFetch();
    }
  }, [searchQuery, previousSearchQuery, resetAndFetch]);

  useEffect(() => {
    if (Object.keys(filters).length > 0 || searchQuery.length > 0) {
      resetAndFetch();
    }
  }, [filters, searchQuery, submittedTimestamp, resetAndFetch]);

  useEffect(() => {
    if (skip) {
      return;
    }

    const hasInitialPage =
      page === 1 &&
      initialData.length > 0 &&
      Object.keys(filters).length === 0 &&
      !searchQuery;

    if (hasInitialPage) {
      return;
    }

    const expectedLength = page * limit;
    const isCached = data.length >= expectedLength && data.length > 0;

    if (!isFetching && !isCached) {
      fetchPage(page, page === 1);
    }
  }, [
    page,
    skip,
    fetchPage,
    isFetching,
    data.length,
    limit,
    initialData.length,
    filters,
    searchQuery,
  ]);

  const isLast = useMemo(() => {
    if (skip) {
      return true;
    }

    if (count === -1) {
      return false;
    }

    return data.length >= count;
  }, [count, data.length, skip]);

  return {
    page,
    data,
    isLast,
    setPage,
    isFetching,
    count: count < 0 ? 0 : count,
  };
};
